import { useEffect, useState } from 'react'
import { Navigate, useLocation, useSearchParams } from 'react-router'
import { startSocialLogin } from '@/features/auth/api'
import { useSession } from '@/features/auth/use-session'
import { LoginScreen, type LoginFailureReason } from '@/features/auth/LoginScreen'
import { useLanguage } from '@/features/language/use-language'
import { PROVIDER_IDS, type ProviderId } from '@/shared/ui/providers'

const FAILURE_REASONS: readonly LoginFailureReason[] = [
  'declined',
  'email_already_registered',
  'email_unavailable',
  'unavailable',
]

function parseReason(value: string | null): LoginFailureReason | null {
  return FAILURE_REASONS.find((reason) => reason === value) ?? null
}

function parseProvider(value: string | null): ProviderId | null {
  return PROVIDER_IDS.find((provider) => provider === value) ?? null
}

interface LocationState {
  from?: string
  /** 앱 안에서 Session이 끊겨 옮겨져 왔는지. 새로고침하면 사라지는 값이라 주소에 남지 않는다. */
  sessionExpired?: boolean
}

/**
 * 로그인 화면에 데이터를 대는 자리.
 *
 * 어떤 안내를 띄울지는 로그인한 곳에서 돌아온 주소에 실린 값이 정한다. 까닭과 어느 곳에서
 * 났는지 둘을 읽는다. 값은 첫 렌더에서 한 번 읽어 두고 주소에서는 곧바로 지운다. 지우지
 * 않으면 새로고침마다 같은 안내를 다시 본다.
 */
export function LoginPage() {
  const session = useSession()
  const { languageId } = useLanguage()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [reason] = useState(() => parseReason(searchParams.get('reason')))
  const [failedProvider] = useState(() => parseProvider(searchParams.get('provider')))
  const [leavingProvider, setLeavingProvider] = useState<ProviderId | null>(null)
  /*
   * 끊김 안내는 주소가 아니라 화면 안에서 옮겨 오며 전한다. 첫 렌더에서 한 번 읽어 두어
   * 뒤에 상태가 사라져도 안내가 그대로 서 있게 한다.
   */
  const [sessionExpired] = useState(
    () => (location.state as LocationState | null)?.sessionExpired === true,
  )

  useEffect(() => {
    if (!searchParams.has('reason') && !searchParams.has('provider')) return
    const next = new URLSearchParams(searchParams)
    next.delete('reason')
    next.delete('provider')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  if (session.status === 'authenticated') {
    const state = location.state as LocationState | null
    return <Navigate to={state?.from ?? '/'} replace />
  }

  return (
    <LoginScreen
      onStart={(provider) => {
        setLeavingProvider(provider)
        startSocialLogin(provider, languageId)
      }}
      status={leavingProvider ? 'leaving' : session.status === 'loading' ? 'establishing' : 'idle'}
      leavingProvider={leavingProvider}
      reason={reason}
      sessionExpired={sessionExpired}
      failedProvider={failedProvider}
    />
  )
}
